import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { personalInfo } from '../data/mockData';
import { COLORS, SPACING } from '../constants/designSystem';
import { Menu, X } from 'lucide-react';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const katherineFont = new FontFace(
      'Katherine', 
      'url(/fonts/Katherine.otf) format("opentype")'
    );
    katherineFont.load().then((loadedFont) => {
      document.fonts.add(loadedFont);
    }).catch((err) => {
      console.error('Failed to load Katherine font:', err);
    });
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navItems = [
    { label: 'Home', path: '/' },
    { label: 'About', path: '/about' },
    { label: 'Projects', path: '/projects' },
    { label: 'Awards', path: '/awards' },
    { label: 'Teaching', path: '/teaching' },
    { label: 'Contact', path: '/contact' },
  ];

  return (
    <header
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 1000,
        backgroundColor: isScrolled ? `${COLORS.background}ee` : 'transparent',
        backdropFilter: isScrolled ? 'blur(20px)' : 'none',
        borderBottom: isScrolled ? `1px solid ${COLORS.border}` : 'none',
        transition: 'all 300ms cubic-bezier(0.4, 0, 0.2, 1)',
      }}
    >

      <nav
        style={{
          maxWidth: '1400px',
          margin: '0 auto',
          padding: `${SPACING.md} ${SPACING.lg}`,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        {/* Logo */}
        <Link
          to="/"
          style={{
            fontSize: '32px',
            fontWeight: '600',
            fontFamily: 'Katherine, sans-serif',
            color: COLORS.text.primary,
            textDecoration: 'underliine',
            display: 'flex',
            alignItems: 'center',
            gap: SPACING.xs,
          }}
        >
          {personalInfo.nickname}
          <span style={{ color: COLORS.primary }}>.</span>
        </Link>

        {/* Desktop Navigation */}
        <div
          style={{
            display: 'flex',
            gap: SPACING.lg,
            alignItems: 'center',
          }}
          className="desktop-nav"
        >
          {navItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              style={{
                color: location.pathname === item.path ? COLORS.primary : COLORS.text.secondary,
                fontSize: '16px',
                fontWeight: '500',
                textDecoration: 'none',
                transition: 'color 300ms cubic-bezier(0.4, 0, 0.2, 1)',
                padding: `${SPACING.xs} 0`,
                position: 'relative',
              }}
              onMouseEnter={(e) => {
                if (location.pathname !== item.path) {
                  e.currentTarget.style.color = COLORS.primary;
                }
              }}
              onMouseLeave={(e) => {
                if (location.pathname !== item.path) {
                  e.currentTarget.style.color = COLORS.text.secondary;
                }
              }}
            >
              {item.label}
              {location.pathname === item.path && (
                <div
                  style={{
                    position: 'absolute',
                    bottom: '-4px',
                    left: 0,
                    right: 0,
                    height: '2px',
                    backgroundColor: COLORS.primary,
                    borderRadius: '2px',
                  }}
                />
              )}
            </Link>
          ))}
        </div>

        {/* Mobile Menu Button */}
        <button
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          style={{
            display: 'none',
            background: 'none',
            border: 'none',
            color: COLORS.text.primary,
            cursor: 'pointer',
            padding: SPACING.xs,
          }}
          className="mobile-menu-btn"
        >
          {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </nav>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div
          style={{
            backgroundColor: COLORS.background,
            borderTop: `1px solid ${COLORS.border}`,
            padding: SPACING.md,
          }}
          className="mobile-menu"
        >
          {navItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              onClick={() => setIsMobileMenuOpen(false)}
              style={{
                display: 'block',
                width: '100%',
                textAlign: 'left',
                color: location.pathname === item.path ? COLORS.primary : COLORS.text.secondary,
                fontSize: '16px',
                fontWeight: '500',
                textDecoration: 'none',
                padding: SPACING.sm,
                transition: 'color 300ms cubic-bezier(0.4, 0, 0.2, 1)',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.color = COLORS.primary;
              }}
              onMouseLeave={(e) => {
                if (location.pathname !== item.path) {
                  e.currentTarget.style.color = COLORS.text.secondary;
                }
              }}
            >
              {item.label}
            </Link>
          ))}
        </div>
      )}

      <style>{`
        @media (max-width: 768px) {
          .desktop-nav {
            display: none !important;
          }
          .mobile-menu-btn {
            display: block !important;
          }
        }
        @media (min-width: 769px) {
          .mobile-menu {
            display: none !important;
          }
        }
      `}</style>
    </header>
  );
};

export default Header;
